import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  dataMetaSelector,
  dispatchGetBandAction,
  editTrial,
} from "Bands/state";
import { BAND_ACTIONS, IBandSubscription } from "Bands/types";
import { useAPIError, useClearActionMetaOnUnMount } from "Bands/utils";
import { copy } from "shared/assets";
import { Edit } from "shared/assets/icons";
import { FormBuilder, Modal } from "shared/components";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";
import {
  IAPIState,
  IFormButtons,
  IJSONAPIErrorCodes,
  IStatus,
} from "shared/types";
import { StatusText } from "shared/styles";
import { dateStringToISO, formatDateTime } from "shared/utils";

import { getFields } from "./formData";

const modalCopy = copy.adminDashboard.editTrial;

interface EditTrialModalProps {
  trial: IBandSubscription;
  handleClose: () => void;
}

export const EditTrialModal = ({ trial, handleClose }: EditTrialModalProps) => {
  const dispatch = useDispatch();
  const { api: apiEditMeta } = useSelector(
    dataMetaSelector(BAND_ACTIONS.editTrial)
  );
  useClearActionMetaOnUnMount(BAND_ACTIONS.editTrial);

  const fields = useMemo(
    () => getFields(!!trial.deactivatedAt),
    [trial.deactivatedAt]
  );

  const defaultValues = useMemo(
    () => ({
      start: formatDateTime(trial.start, { dateOnly: true }),
      stop: formatDateTime(trial.stop, { dateOnly: true }),
    }),
    [trial.start, trial.stop]
  );

  const buttons: IFormButtons = useMemo(
    () => ({
      primary: {
        "data-testid": "confirm_edit_trial",
      },
      secondary: {
        onClick: handleClose,
        "data-testid": "cancel_edit_trial",
      },
    }),
    [handleClose]
  );

  const onSubmit = (data: { start?: string; stop: string }) => {
    dispatch(
      editTrial({
        subscriptionId: trial.id,
        ...(data.start && {
          start: `${dateStringToISO(data.start)}T00:00:00+00:00`,
        }),
        stop: `${dateStringToISO(data.stop)}T23:59:59+00:00`,
      })
    );
  };

  const { error } = useAPIError({
    apiError: apiEditMeta?.error,
    message: modalCopy.dateNotInFuture,
    errorCode: IJSONAPIErrorCodes.NumericTooLowError,
  });

  useEffect(() => {
    if (apiEditMeta?.status === IAPIState.succeeded) {
      // refetch band to update subscription
      if (trial.bands[0].id) {
        dispatchGetBandAction(dispatch, trial.bands[0].id);
      }

      handleClose();

      dispatch(
        addTemporaryToast({
          message: modalCopy.success,
          status: IStatus.success,
        })
      );
    }
  }, [dispatch, apiEditMeta, handleClose, trial.bands]);

  return (
    <Modal handleClose={handleClose} title={modalCopy.title} icon={Edit}>
      <Spacer size="m">
        {!!trial.deactivatedAt && (
          <StatusText status={IStatus.error}>{modalCopy.cancelled}</StatusText>
        )}
        <FormBuilder
          fields={fields}
          onSubmit={onSubmit}
          submitMode="isDirty"
          defaultValues={defaultValues}
          buttons={buttons}
          isSubmitting={apiEditMeta?.status === IAPIState.pending}
          apiErrorMessage={error}
          apiError={apiEditMeta?.error}
        />
      </Spacer>
    </Modal>
  );
};
